import { useMemo } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router';
import { SalesforceAutomation } from 'api/integrations';
import { toastService } from 'components/molecules/toast';
import { useTranslation } from 'hooks/use-translation';
import {
  AUTOMATIONS_LIST_QUERY_KEY,
  useDeleteAutomationMutation,
  useReorderAutomationMutation,
  useSalesForceAutomationsList,
  useUpdateAutomationMutation,
} from 'queries/salesforce-integration';
import { getSalesforceAutomationEditUrl } from 'utils/urls';

export const useAutomationsListActions = () => {
  const history = useHistory();

  const successTitle = useTranslation('success');
  const deleteSuccessMessage = useTranslation(
    'settings.integrations.sf.automation.delete.success.message',
  );
  const updateSuccessMessage = useTranslation(
    'settings.integrations.sf.automation.updated.success.message',
  );

  const queryClient = useQueryClient();
  const { data: automationList, status: automationListStatus } =
    useSalesForceAutomationsList();
  const { mutate: updateAutomationMutation } = useUpdateAutomationMutation();
  const { mutate: updateDeleteMutation } = useDeleteAutomationMutation();
  const { mutate: reorderAutomationMutation } = useReorderAutomationMutation();

  const onChange = async (updatedAutomationDetails: SalesforceAutomation) => {
    const fallbackValue = automationList?.find(
      (automation) =>
        automation.automationId === updatedAutomationDetails.automationId,
    );

    if (fallbackValue == null) {
      return false;
    }

    // updating local state first
    await queryClient.setQueryData(
      AUTOMATIONS_LIST_QUERY_KEY,
      (savedAutomations) =>
        (savedAutomations as SalesforceAutomation[]).map((automation) => {
          if (
            automation.automationId === updatedAutomationDetails.automationId
          ) {
            return updatedAutomationDetails;
          }

          return automation;
        }),
    );

    return updateAutomationMutation(updatedAutomationDetails, {
      onSuccess: () => {
        toastService({
          toastContainer: {},
          title: successTitle,
          message: updateSuccessMessage,
        }).success();
      },
      onError: () => {
        queryClient.setQueryData(
          AUTOMATIONS_LIST_QUERY_KEY,
          (savedAutomations) =>
            (savedAutomations as SalesforceAutomation[]).map((automation) => {
              if (automation.automationId === fallbackValue.automationId) {
                return fallbackValue;
              }

              return automation;
            }),
        );
      },
    });
  };

  const onDelete = async (automationId: number) => {
    const deletedAutomation = automationList?.find(
      (automation) => automation.automationId === automationId,
    );

    if (deletedAutomation == null) {
      return false;
    }

    // updating local state first
    await queryClient.setQueryData(
      AUTOMATIONS_LIST_QUERY_KEY,
      (savedAutomations) =>
        (savedAutomations as SalesforceAutomation[]).filter(
          (automation) => automation.automationId !== automationId,
        ),
    );

    return updateDeleteMutation(automationId, {
      onSuccess: () => {
        toastService({
          toastContainer: {},
          title: successTitle,
          message: deleteSuccessMessage,
        }).success();
      },
      onError: () => {
        queryClient.setQueryData(
          AUTOMATIONS_LIST_QUERY_KEY,
          (savedAutomations: SalesforceAutomation[] | undefined) => [
            ...(savedAutomations ?? []),
            deletedAutomation,
          ],
        );
      },
    });
  };

  const onEdit = (integraitonId: string, automationId: number) => {
    history.push(getSalesforceAutomationEditUrl(integraitonId, automationId));
  };

  const onSortChange = async (
    updatedAutomationsList: SalesforceAutomation[],
    updatedAutomation?: SalesforceAutomation,
    newOrder?: number,
  ) => {
    const sortedAutomationsList = updatedAutomationsList.map(
      (automation, i) => ({ ...automation, order: i + 1 }),
    );

    await queryClient.setQueryData(
      AUTOMATIONS_LIST_QUERY_KEY,
      sortedAutomationsList,
    );

    if (updatedAutomation && (newOrder || newOrder === 0)) {
      reorderAutomationMutation({
        automationId: Number(updatedAutomation.automationId),
        automationUpdatedOrder: newOrder,
      });
    }
  };

  const sortedAutomationsList = useMemo(
    () => automationList?.sort((a, b) => a.order - b.order),
    [automationList],
  );

  return {
    automationList: sortedAutomationsList,
    automationListStatus,
    onSortChange,
    onDelete,
    onChange,
    onEdit,
  };
};
